import { Observable } from 'rxjs';

import { ReconnectingWebSocket } from '../utils/reconnecting-websocket';
import { AppLifecycleAdapter } from './app-lifecycle-adapter';

export interface SocketAdapter {
  connect(url: string, resetCounter?: boolean): Promise<void>;
  send(event: string, payload: unknown): Promise<void>;
  disconnect(): Promise<void>;
  // See https://developer.mozilla.org/docs/Web/API/WebSocket/readyState
  readyState(): Promise<number>;

  // Single event stream - ALL events flow through here
  events$: Observable<{ event: string; payload?: unknown }>;

  /**
   * True when this adapter owns the real socket; false for an IPC proxy whose
   * events originated in — and were already handled and logged by — another
   * process.
   *
   * SharedSocketService runs in both the Electron main process and the
   * renderer. Only the owner should do connection-owner work: heartbeat,
   * joining the user channel, and logging event payloads. A non-owner that
   * repeats any of it puts a duplicate ping on the wire, joins a channel that
   * is already joined, and re-prints a payload the owner already printed.
   */
  readonly ownsConnection: boolean;
}

/**
 * Creates a {@link ReconnectingWebSocket} (dumb transport + retry loop) and
 * bridges its callbacks to the adapter's `{ event, payload }` stream. Mirrors
 * the legacy event mapping: open -> 'connect', close -> 'disconnect',
 * error -> 'connect_error', and JSON messages -> their own event name.
 *
 * The loop owns reconnects and pulls a fresh, token-bearing URL from
 * `urlProvider` before every attempt, so callers never push a URL.
 */
export function createReconnectingSocket(
  urlProvider: () => Promise<string>,
  lifecycleAdapter: AppLifecycleAdapter,
  onEvent: (event: string, payload?: unknown) => void,
  options?: { handshakeHeaders?: Record<string, string> }
): ReconnectingWebSocket {
  console.log('[SOCKET-ADAPTER] Creating ReconnectingWebSocket');
  const ws = new ReconnectingWebSocket({ urlProvider, lifecycleAdapter, handshakeHeaders: options?.handshakeHeaders });

  ws.onOpen = () => {
    console.log('[SOCKET-ADAPTER] WebSocket opened');
    onEvent('connect');
  };

  ws.onClose = () => {
    console.log('[SOCKET-ADAPTER] WebSocket closed');
    onEvent('disconnect');
  };

  ws.onError = (event) => {
    console.error('[SOCKET-ADAPTER] WebSocket error:', event);
    onEvent('connect_error', event);
  };

  ws.onMessage = (data) => {
    try {
      const parsed = JSON.parse(data);
      if (
        parsed &&
        Object.prototype.hasOwnProperty.call(parsed, 'event') &&
        Object.prototype.hasOwnProperty.call(parsed, 'payload')
      ) {
        // Arrival is logged once, by SharedSocketService. Logging it here too
        // just repeats the same fact one layer down.
        onEvent(parsed.event, parsed.payload);
      } else {
        console.log('[SOCKET-ADAPTER] Message format unexpected:', parsed);
      }
    } catch (error) {
      console.error('[SOCKET-ADAPTER] Failed to parse message:', error, 'Raw:', data);
    }
  };

  ws.start();
  console.log('[SOCKET-ADAPTER] ReconnectingWebSocket created and started');
  return ws;
}
